import { useMemo, useState, useEffect, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useMatch,
  useLocation,
  useNavigate,
  matchPath,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import LinearProgress from "@mui/material/LinearProgress";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Toaster, toast } from "sonner";

import { lightTheme, darkTheme } from "./theme";
import { COMPONENT_HEIGHTS, MOBILE_OR_COARSE_QUERY } from "./types/tokens";
import { useConnectionStore } from "./store/useConnectionStore";
import { useThemeStore } from "./store/useThemeStore";
import { useUIStore } from "./store/useUIStore";
import { useDashboardHistoryStore } from "./store/useDashboardHistoryStore";
import { useDashboardEditorStore } from "./store/useDashboardEditorStore";
import { useResetAllStores } from "./hooks/useResetAllStores";
import { useShallow } from "zustand/react/shallow";
import { useSessionResume } from "./hooks/useSessionResume";
import AppHeader from "./components/AppHeader";
import AppSidebar from "./components/AppSidebar";
import AiAssistantDrawer from "./components/AiAssistantDrawer";
import ParameterBar from "./components/ParameterBar";
import ConnectionDialog from "./components/ConnectionDialog";
import ResetConfirmationDialog from "./components/ResetConfirmationDialog";
import PanelEditor from "./components/PanelEditor";
import CommandPalette from "./components/CommandPalette";
import DashboardViewPage from "./components/DashboardViewPage";
import WelcomeScreen from "./components/WelcomeScreen";
import ContentSkeleton from "./components/ContentSkeleton";
import ErrorBoundary from "./components/ErrorBoundary";
import PersesProviders from "./components/perses/PersesProviders";
import LLMKeyNudgeBanner from "./components/LLMKeyNudgeBanner";
import InsightStatusFooter from "./components/InsightStatusFooter";
import IsometricMap from "./features/easterEgg/IsometricMap";
import GoToMapFab from "./features/easterEgg/GoToMapFab";
import { getMatchedPageId } from "./features/easterEgg/routeMatching";
import { PAGE_PATHS } from "./routes/paths";
import { useEasterEggStore } from "./store/useEasterEggStore";
import { PAGE_MANIFEST } from "./routes/manifest";

const currentYear = new Date().getFullYear();

export default function App() {
  const themeMode = useThemeStore((s) => s.themeMode);
  const setConnectionDialogOpen = useUIStore((s) => s.setConnectionDialogOpen);
  const { easterEggMode, markPageVisited } = useEasterEggStore(
    useShallow((s) => ({
      easterEggMode: s.easterEggMode,
      markPageVisited: s.markPageVisited,
    })),
  );
  const connected = useConnectionStore((s) => s.connected);
  const resetState = useResetAllStores();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const handleRequestReset = () => setResetDialogOpen(true);
  const theme = useMemo(() => (themeMode === "dark" ? darkTheme : lightTheme), [themeMode]);
  const dashboardTimeZone = useDashboardEditorStore((s) => s.dashboard.timeZone);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDashboardView = Boolean(useMatch("/dashboards/:id"));
  const { resumeError, clearResumeError } = useSessionResume();

  const { undoDashboardChange, redoDashboardChange } = useDashboardHistoryStore(
    useShallow((s) => ({
      undoDashboardChange: s.undoDashboardChange,
      redoDashboardChange: s.redoDashboardChange,
    })),
  );

  const location = useLocation();
  useEffect(() => {
    const match = Object.values(PAGE_PATHS).find((p) => matchPath(p.path, location.pathname));
    if (match) {
      document.title = `${match.nav.label} — Elastic Peek`;
      return;
    }
    if (matchPath("/dashboards/:id", location.pathname)) {
      document.title = "Dashboards — Elastic Peek";
      return;
    }
    document.title = "Elastic Peek";
  }, [location.pathname]);

  useEffect(() => {
    if (!connected || !easterEggMode) return;
    const pageId = getMatchedPageId(location.pathname);
    if (pageId) markPageVisited(pageId);
  }, [connected, easterEggMode, location.pathname, markPageVisited]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === "z";
      const isRedo =
        (e.ctrlKey || e.metaKey) &&
        ((e.shiftKey && e.key.toLowerCase() === "z") || (!e.shiftKey && e.key === "y"));
      if (!isUndo && !isRedo) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const inEditableRegion =
        target?.isContentEditable ||
        Boolean(
          target?.closest('[contenteditable="true"],[contenteditable=""],.cm-editor,.cm-content'),
        );
      if (tag === "INPUT" || tag === "TEXTAREA" || inEditableRegion) return;
      e.preventDefault();
      if (isUndo) undoDashboardChange();
      else redoDashboardChange();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undoDashboardChange, redoDashboardChange]);

  useEffect(() => {
    if (!resumeError) return;
    toast.warning(`Could not resume session: ${resumeError}`, {
      action: {
        label: "Reconnect",
        onClick: () => {
          setConnectionDialogOpen(true);
        },
      },
    });
    clearResumeError();
  }, [resumeError, clearResumeError, setConnectionDialogOpen]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PersesProviders timeZone={dashboardTimeZone}>
        <Box
          sx={{ display: "flex", flexDirection: "column", height: "100vh", overflowX: "hidden" }}
        >
          <AppHeader
            showMobileNavToggle={connected && isMobile}
            onToggleMobileNav={() => setMobileNavOpen((prev) => !prev)}
          />
          <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflowX: "hidden" }}>
            {connected &&
              (isMobile ? (
                <Drawer
                  anchor="left"
                  open={mobileNavOpen}
                  onClose={() => setMobileNavOpen(false)}
                  variant="temporary"
                  sx={{ zIndex: 1202 }}
                >
                  <AppSidebar
                    mobile
                    onNavigate={() => setMobileNavOpen(false)}
                    onRequestReset={handleRequestReset}
                  />
                </Drawer>
              ) : (
                <AppSidebar
                  collapsed={sidebarCollapsed}
                  onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
                  onRequestReset={handleRequestReset}
                />
              ))}
            <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
              {connected && <LLMKeyNudgeBanner onOpenSettings={() => navigate("/settings")} />}
              {connected && isDashboardView && <ParameterBar />}
              <Box
                component="main"
                sx={{
                  position: "relative",
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  minHeight: 0,
                  overflowX: "hidden",
                  overflowY: "auto",
                  p: { sm: 1.5, xs: 1 },
                }}
              >
                <Routes>
                  {Object.entries(PAGE_MANIFEST).map(([pageId, PageComponent]) => {
                    const typedPageId = pageId as keyof typeof PAGE_PATHS;
                    const pageConfig = PAGE_PATHS[typedPageId];
                    const skeletonVariant = pageConfig.skeletonVariant;
                    const fallback =
                      skeletonVariant && skeletonVariant !== "linear" ? (
                        <ContentSkeleton variant={skeletonVariant} />
                      ) : (
                        <LinearProgress />
                      );
                    return (
                      <Route
                        key={pageConfig.path}
                        path={pageConfig.path}
                        element={
                          !connected && pageConfig.requiresConnection ? (
                            <WelcomeScreen />
                          ) : (
                            <ErrorBoundary>
                              <Suspense fallback={fallback}>
                                <PageComponent />
                              </Suspense>
                            </ErrorBoundary>
                          )
                        }
                      />
                    );
                  })}
                  <Route
                    path="/dashboards/:id"
                    element={
                      !connected ? (
                        <WelcomeScreen />
                      ) : (
                        <ErrorBoundary>
                          <DashboardViewPage />
                        </ErrorBoundary>
                      )
                    }
                  />
                  <Route path="/" element={<Navigate to="/dashboards" replace />} />
                  <Route path="*" element={<Navigate to="/dashboards" replace />} />
                </Routes>
              </Box>
              <Box
                component="footer"
                sx={{
                  position: "relative",
                  display: "flex",
                  flexShrink: 0,
                  gap: 1,
                  justifyContent: "center",
                  alignItems: "center",
                  py: 0.5,
                  px: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Link
                  href="https://github.com/elastic/ai-github-actions-playground"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: "warning.main",
                    color: "common.black",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    "&:hover": { bgcolor: "warning.dark" },
                  }}
                >
                  Research Project
                </Link>
                <Typography variant="caption" color="text.secondary">
                  Not an official product &mdash; &copy; {currentYear}{" "}
                  <Link
                    href="https://www.elastic.co"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="hover"
                    sx={{
                      [MOBILE_OR_COARSE_QUERY]: {
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: COMPONENT_HEIGHTS.touchTarget,
                      },
                    }}
                  >
                    Elasticsearch B.V.
                  </Link>
                </Typography>
                {connected && <InsightStatusFooter />}
              </Box>
            </Box>
            {connected && (
              <ErrorBoundary>
                <AiAssistantDrawer isMobile={isMobile} />
              </ErrorBoundary>
            )}
          </Box>
        </Box>
        <ConnectionDialog />
        <ResetConfirmationDialog
          open={resetDialogOpen}
          onConfirm={() => {
            resetState();
            navigate("/dashboards", { replace: true });
            setResetDialogOpen(false);
          }}
          onCancel={() => setResetDialogOpen(false)}
        />
        <ErrorBoundary>
          <PanelEditor />
        </ErrorBoundary>
        <CommandPalette />
        {connected && easterEggMode && (
          <ErrorBoundary>
            <IsometricMap />
            <GoToMapFab />
          </ErrorBoundary>
        )}
        <Toaster theme={themeMode} position="bottom-left" />
      </PersesProviders>
    </ThemeProvider>
  );
}
